import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { type Lead, type ActivityLog, fmtDateTime } from "../types";

interface Props {
  lead: Lead;
  tenantId: string;
}

const ACTION_CFG: Record<string, { icon: string; color: string; label: string }> = {
  created: { icon: "ri-add-circle-line",   color: "bg-emerald-100 text-emerald-600", label: "Created" },
  updated: { icon: "ri-edit-circle-line",  color: "bg-amber-100 text-amber-600",     label: "Updated" },
  deleted: { icon: "ri-delete-bin-line",   color: "bg-red-100 text-red-500",         label: "Deleted" },
  viewed:  { icon: "ri-eye-line",          color: "bg-slate-100 text-slate-500",     label: "Viewed" },
  sent:    { icon: "ri-send-plane-line",   color: "bg-sky-100 text-sky-600",         label: "Sent" },
};

const ENTITY_LABEL: Record<string, string> = {
  client:      "Client",
  job:         "Job",
  contract:    "Contract",
  appointment: "Appointment",
  lead:        "Lead",
  payment:     "Payment",
};

function getCfg(action: string) {
  return ACTION_CFG[action] ?? { icon: "ri-checkbox-blank-circle-line", color: "bg-slate-100 text-slate-400", label: action };
}

export default function ActivityTab({ lead, tenantId }: Props) {
  const [logs, setLogs]       = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    // Two separate queries to avoid .or() UUID parsing issues
    const [byId, byName] = await Promise.all([
      supabase
        .from("activity_logs")
        .select("*")
        .eq("tenant_id", tenantId)
        .eq("entity_id", lead.id)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("activity_logs")
        .select("*")
        .eq("tenant_id", tenantId)
        .ilike("entity_name", `%${lead.name}%`)
        .order("created_at", { ascending: false })
        .limit(30),
    ]);

    // Merge + deduplicate by id
    const merged = [...(byId.data ?? []), ...(byName.data ?? [])];
    const seen   = new Set<string>();
    const unique = merged.filter((l) => {
      if (seen.has((l as ActivityLog).id)) return false;
      seen.add((l as ActivityLog).id);
      return true;
    });
    setLogs(unique as ActivityLog[]);
    setLoading(false);
  }, [tenantId, lead.id, lead.name]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  /* Build a timeline: creation event first, then logs */
  const creationEntry = {
    id:          "creation",
    created_at:  lead.created_at,
    action:      "created",
    entity:      "lead",
    entity_name: lead.name,
    entity_id:   lead.id,
    tenant_id:   tenantId,
    user_id:     null,
  } as ActivityLog;

  const timeline = [
    ...logs,
    ...(logs.some((l) => l.entity === "lead" && l.entity_id === lead.id) ? [] : [creationEntry]),
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100">
          <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider">Activity Timeline</h3>
        </div>

        {timeline.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-100">
              <i className="ri-time-line text-slate-300 text-xl"></i>
            </div>
            <p className="text-slate-400 text-sm font-medium">No activity recorded yet</p>
            <p className="text-slate-300 text-xs max-w-xs">
              Actions like converting to a client, creating jobs or contracts will appear here.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {timeline.map((log, idx) => {
              const cfg = getCfg(log.action);
              const entityLabel = ENTITY_LABEL[log.entity] ?? log.entity;
              return (
                <div key={log.id ?? idx} className="flex items-start gap-3 px-5 py-3.5">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${cfg.color}`}>
                    <i className={`${cfg.icon} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-slate-700 text-sm">
                          <span className="font-medium">{cfg.label}</span>
                          {" "}
                          <span className="text-slate-500">{entityLabel}</span>
                          {log.entity_name && log.entity !== "lead" && (
                            <span className="text-slate-400 text-xs"> — &quot;{log.entity_name}&quot;</span>
                          )}
                        </p>
                      </div>
                      <span className="text-slate-300 text-[10px] whitespace-nowrap shrink-0">
                        {fmtDateTime(log.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lead creation summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-3">Lead Summary</h3>
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Lead created</span>
            <span className="text-slate-600">{fmtDateTime(lead.created_at)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Current status</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
              lead.status === "new"       ? "bg-sky-50 text-sky-600 border-sky-200" :
              lead.status === "contacted" ? "bg-amber-50 text-amber-600 border-amber-200" :
              lead.status === "booked"    ? "bg-emerald-50 text-emerald-600 border-emerald-200" :
              "bg-red-50 text-red-600 border-red-200"
            }`}>
              {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Total activity events</span>
            <span className="text-slate-600 font-medium">{timeline.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}